import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import XLSX from 'xlsx';
import {
  Tooltip,
  Popover as NativePopover,
  List,
  ListItem,
  ListItemIcon,
  ListItemText
} from '@material-ui/core';
import PublishIcon from '@material-ui/icons/Publish';
import EqualizerIcon from '@material-ui/icons/Equalizer';
import HistoryIcon from '@material-ui/icons/History';
import AddIcon from '@material-ui/icons/Add';
import PlaylistAddIcon from '@material-ui/icons/PlaylistAdd';
import GetAppIcon from '@material-ui/icons/GetApp';
import DeleteIcon from '@material-ui/icons/Delete';
import { TableEditBar, Modal, Popover } from './components';
import { DeleteModal } from 'components';
import Table from 'components/Table/Table';
import TableAction from 'components/Table/TableAction';
import { GroupRepoDTO } from 'dto/GroupDTO';
import LoginHistoryItemDTO from 'dto/LoginHistoryItemDTO';
import LoginHistoryGraph from 'views/Profile/LoginHistoryGraph';
import classApi from 'services/classes'
import FullscreenDialog from 'components/FullscreenDialog';
import Loading from 'components/Loading/Loading';
import useConfirmation from 'utils/useConfirmation';
import TableDownloadAction from 'components/Table/TableDownloadAction';
import moment from 'moment';

interface MembersProps {
  className?: string;
  group: GroupRepoDTO;
  title?: string;
  onRemoveMembers: (classID: number, selectedStudents: number[], handleDeselect: () => void) => void;
  onSendMailStudents: (classID: number) => () => void;
  onAddMember: (classID: number) => (data: { emails: string[] }, callback: () => void) => void;
}

const Members = (props: MembersProps) => {
  const { className, onAddMember, onRemoveMembers, onSendMailStudents, group, title } = props;

  const addMemberRef = useRef(null);
  const importMemberRef = useRef(null);
  const uploadRef = useRef(null);
  const [open, setOpen] = useState(true);
  const [openDelete, setOpenDelete] = useState(false);
  const [openImportMember, setOpenImportMember] = useState(false);
  const [openAddMember, setOpenAddMember] = useState(false);
  const [openSendMail, setOpenSendMail] = useState(false);
  const [loginHistoryOpen, setLoginHistoryOpen] = useState<boolean>(false);
  const [loginHistoryTableOpen, setLoginHistoryTableOpen] = useState<boolean>(false);
  const [loginHistoryData, setLoginHistoryData] = useState<LoginHistoryItemDTO[]>(null);
  const [selectedStudents, setSelectedStudents] = useState([]);
  const confirmation = useConfirmation({
    message: 'An invitation will be sent out to all imported users',
    actionYes: 'Ok',
    actionNo: 'Cancel'
  })

  useEffect(() => {
    setSelectedStudents([]);
  }, [group.classID])


  const handleDeselect = () => {
    setSelectedStudents([]);
    setOpenDelete(false);
  };

  const getLoginHistory = async (member: GroupRepoDTO['students'][number]) => {
    const data = await classApi.getStudentLoginHistory(member.id);
    setLoginHistoryData(data);
  }

  const handleLoginHistoryOpen = async (member: GroupRepoDTO['students'][number]) => {
    setLoginHistoryOpen(true);
    await getLoginHistory(member);
  }

  const handleLoginHistoryTableOpen = async (member: GroupRepoDTO['students'][number]) => {
    setLoginHistoryTableOpen(true);
    await getLoginHistory(member);
  }

  const handlePopoverClose = () => {
    setOpenImportMember(false);
  };

  const handleUpload = ({ target }) => {
    setOpenImportMember(false);
    const { files: [file] } = target;
    const reader = new FileReader();
    reader.onload = ({ target }) => {
      const { SheetNames: [firstSheetName], Sheets } = XLSX.read(new Uint8Array(target.result as ArrayBuffer), { type: 'array' });
      const firstSheet = Sheets[firstSheetName];
      const importStudents = XLSX.utils.sheet_to_json(firstSheet)
        .filter(student => typeof student[Object.keys(student)[0]] === 'string')
        .map(student => student[Object.keys(student)[0]].toLowerCase().trim())
        .filter(email => email && !group.students.some(student => student.email.toLowerCase() === email));
      if (importStudents.length) {
        onAddMember(group.classID)({ emails: [...new Set(importStudents)] }, handlePopoverClose);
      }
    }
    reader.readAsArrayBuffer(file);
  };

  const handleDeleteModal = () => {
    onRemoveMembers(group.classID, selectedStudents, handleDeselect)
  };

  return (
    <>
      <Table
        actions={<>
          <TableDownloadAction data={group.students} headers={['firstName', 'lastName', 'email']} fileName={`${group.name} Members.csv`} />
          <TableAction
            onClick={(e) => {
              setOpenImportMember(true)
              importMemberRef.current = e.target;
            }}
            title='Invite Members'>
            <PlaylistAddIcon />
          </TableAction>
          <TableAction
            onClick={(e) => {
              setOpenAddMember(true)
              addMemberRef.current = e.target;
            }}
            title='Invite Member'>
            <AddIcon />
          </TableAction>
        </>}
        cellMap={{
          firstName: row => `${row.firstName} ${row.lastName}`
        }}
        classes={{root: className}}
        data={group.students}
        filters={{
          email: {
            type: 'input'
          },
          firstName: {
            type: 'input'
          }
        }}
        headerMap={{
          firstName: 'name',
          email: 'E-Mail'
        }}
        headers={['firstName', 'email']}
        idKey='id'
        itemActions={[{
          Icon: <EqualizerIcon/>,
          onClick: (row) => handleLoginHistoryOpen(row)
        }, {
          Icon: <HistoryIcon/>,
          onClick: (row) => handleLoginHistoryTableOpen(row)
        }]}
        open={open}
        selectable
        selectedIds={selectedStudents}
        setOpen={setOpen}
        setSelectedIds={setSelectedStudents}
        title={title}

      />
      <TableEditBar
        onDelete={() => setOpenDelete(true)}
        onSendMail={() => setOpenSendMail(true)}
        selected={selectedStudents}
      />
      <Modal
        onClose={() => setOpenSendMail(false)}
        onSendMailStudents={onSendMailStudents(group.classID)}
        open={openSendMail}
        selected={group.students.filter(student => selectedStudents.includes(student.id))}
      />
      <Popover
        anchorEl={addMemberRef.current}
        onClose={() => setOpenAddMember(false)}
        onSubmit={onAddMember(group.classID)}
        open={openAddMember}
      />
      <NativePopover
        anchorEl={importMemberRef.current}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center'
        }}
        onClose={handlePopoverClose}
        open={openImportMember}
      >
        <List>
          <Tooltip title="Invite members file">
            <ListItem
              button
              divider
              onClick={async () => {
                await confirmation();
                uploadRef.current.click()
              }}
            >
              <input
                accept=".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
                onChange={handleUpload}
                ref={uploadRef}
                style={{ display: 'none' }}
                type="file"
                value=""
              />
              <ListItemIcon>
                <PublishIcon />
              </ListItemIcon>
              <ListItemText primary="Import" />
            </ListItem>
          </Tooltip>
          <Tooltip title="Download CSV template">
            <ListItem
              button
              component="a"
              download
              href="/StudentListTemplate.csv"
              onClick={handlePopoverClose}
            >
              <ListItemIcon>
                <GetAppIcon />
              </ListItemIcon>
              <ListItemText
                primary="CSV Template"
              />
            </ListItem>
          </Tooltip>
          <Tooltip title="Download Excel template">
            <ListItem
              button
              component="a"
              download
              href="/StudentListTemplate.xlsx"
              onClick={handlePopoverClose}
            >
              <ListItemIcon>
                <GetAppIcon />
              </ListItemIcon>
              <ListItemText
                primary="Excel Template"
              />
            </ListItem>
          </Tooltip>
        </List>
      </NativePopover>
      <DeleteModal
        button="Remove"
        buttonIcon={<DeleteIcon />}
        content="Are you sure you want to remove the selected member(s)?"
        onClose={() => setOpenDelete(false)}
        onDelete={handleDeleteModal}
        open={openDelete}
        title={`${selectedStudents.length} Member(s)`}
      />
      <FullscreenDialog
        onClose={() => setLoginHistoryOpen(null)}
        onClosed={() => setLoginHistoryData(null)}
        open={loginHistoryOpen}
      >
        {loginHistoryData ? <LoginHistoryGraph data={loginHistoryData} />:<Loading/>}
      </FullscreenDialog>
      <FullscreenDialog
        onClose={() => setLoginHistoryTableOpen(false)}
        onClosed={() => setLoginHistoryData(null)}
        open={loginHistoryTableOpen}
      >
        {loginHistoryData ? 
          <Table
            actions={<>
              <TableDownloadAction data={loginHistoryData} fileName='Login History.csv' />
            </>}
            cellMap={{
              duration: (row) => {
                let out = '';
                const d = moment.duration(row.duration);
                if(d.days() > 0) {
                  out += `${d.days()}D `
                }
                if(d.hours() > 0) {
                  out += `${d.hours()}H `
                }
                if(d.minutes() > 0) {
                  out += `${d.minutes()}M ` 
                }
                out += `${d.seconds()}S`;
            
                return out;
              },
              login: (row) => row.login.local().format('MMM DD, YYYY hh:mm A'),
              logout: (row) => row.logout.local().format('MMM DD, YYYY hh:mm A'),
              expired: (row) => {
                switch (row.expired) {
                  case true:
                    return 'Expired'
                  case false:
                    return 'Logged out'
                  case null:
                    return 'Still Active'
                }
              }
            }}
            data={loginHistoryData}
            filters={{
              duration: {
                type: 'numeric',
                helperText: 'Duration is in minutes',
                transformation: (data: {to: number, from: number}) => ({to: data.to ? data.to * 60 * 1000:undefined, from: data.from ? data.from * 60 * 1000:data.from})
              } as const,
              login: {
                type: 'date'
              } as const,
              logout: {
                type: 'date'
              } as const,
              expired: {
                type: 'options' as const,
                options: [{
                  value: 'Expired',
                  key: true
                }, {
                  value: 'Logged out',
                  key: false
                }, {
                  value: 'Still Active',
                  key: null
                }]
              }
            }}
            headerMap={{
              expired: 'Session End Reason'
            }}
            headers={['duration', 'login', 'logout', 'expired']}
            idKey='id'
            sortable={['duration', 'login', 'logout']}
            title='Login History'
          />
          : <Loading/>}
      </FullscreenDialog>
    </>
  );
};

Members.propTypes = {
  className: PropTypes.string,
  group: PropTypes.object.isRequired,
  onAddMember: PropTypes.func,
  onRemoveMembers: PropTypes.func,
  onSendMailStudents: PropTypes.func,
  title: PropTypes.string
};

Members.defaultProps = {
  group: {}
};

export default Members;
